import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
} from '@merritrac/shared/components';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Calendar,
  Camera,
  CheckCircle2,
  ClipboardList,
  Clock,
  Download,
  ImageIcon,
  Loader2,
  MapPin,
  MessageSquare,
  Phone,
  User,
  Wrench,
  XCircle,
} from 'lucide-react';
import { lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useGraphQL } from '../hooks/useGraphQL.js';

import type { PdfServiceOrder } from '../components/ServiceReportPdf.js';
import type { JSX, ReactElement } from 'react';

// Lazy-load the PDF components to avoid ~300KB bundle impact on other pages
const PdfDownload = lazy(async () => {
  const [pdf, report] = await Promise.all([
    import('@react-pdf/renderer'),
    import('../components/ServiceReportPdf.js'),
  ]);
  // Return a component that wraps PDFDownloadLink with our report doc
  return {
    default: function PdfDownloadButton({
      order,
      filename,
    }: {
      order: PdfServiceOrder;
      filename: string;
    }): JSX.Element {
      return (
        <pdf.PDFDownloadLink
          document={<report.ServiceReportPdf order={order} />}
          fileName={filename}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg glass text-text transition-all hover:opacity-80"
        >
          {({ loading: pdfLoading }) => (pdfLoading ? 'Generating...' : 'Download PDF')}
        </pdf.PDFDownloadLink>
      );
    },
  };
});

/* ── GraphQL ── */

const GET_SERVICE_ORDER = `
  query GetServiceOrder($accessKey: Float!) {
    serviceOrder(accessKey: $accessKey) {
      accessKey
      ticketno
      description
      notes
      status
      priority
      assignedTo
      createdDate
      dueDate
      completedDate
      client {
        clientCode
        clientName
        contact
        address
        city
        state
        zip
        phone
        email
      }
      units {
        accessKey
        unitNo
        floor
        serviceComplete
        accessDenied
        location
      }
      timeRecords {
        id
        timeStart
        timeStop
        cummulative
        status
        notes
        timeLocation {
          clockInTime
          clockOutTime
          clockInAddress
          clockOutAddress
        }
      }
      images {
        id
        cfImageId
        thumbnailUrl
        filename
        uploadedBy
        uploadedDate
      }
    }
  }
`;

/* ── Types ── */

type OrderStatus = 'pending' | 'in-progress' | 'completed' | 'cancelled';
type GlassBadgeVariant = 'info' | 'success' | 'warning' | 'danger' | 'default';

interface TimeLocationNode {
  clockInTime: string | null;
  clockOutTime: string | null;
  clockInAddress: string | null;
  clockOutAddress: string | null;
}

interface TimeRecordNode {
  id: number;
  timeStart: string;
  timeStop: string | null;
  cummulative: number | null;
  status: string | null;
  notes: string | null;
  timeLocation: TimeLocationNode | null;
}

interface UnitNode {
  accessKey: number;
  unitNo: string;
  floor: string | null;
  serviceComplete: boolean;
  accessDenied: boolean;
  location: string | null;
}

interface ImageNode {
  id: number;
  cfImageId: string | null;
  thumbnailUrl: string | null;
  filename: string;
  uploadedBy: string | null;
  uploadedDate: string;
}

interface ClientNode {
  clientCode: string;
  clientName: string;
  contact: string | null;
  address: string | null;
  city: string;
  state: string;
  zip: string | null;
  phone: string | null;
  email: string | null;
}

interface ServiceOrderNode {
  accessKey: number;
  ticketno: string;
  description: string;
  notes: string | null;
  status: string;
  priority: string;
  assignedTo: string;
  createdDate: string;
  dueDate: string | null;
  completedDate: string | null;
  client: ClientNode | null;
  units: UnitNode[];
  timeRecords: TimeRecordNode[];
  images: ImageNode[];
}

interface ServiceOrderData {
  serviceOrder: ServiceOrderNode | null;
}

/* ── Helpers ── */

function normaliseStatus(raw: string): OrderStatus {
  const lower = raw.toLowerCase();
  if (lower === 'in-progress' || lower === 'inprogress' || lower === 'in progress') {
    return 'in-progress';
  }
  if (lower === 'completed' || lower === 'complete') {
    return 'completed';
  }
  if (lower === 'cancelled' || lower === 'canceled') {
    return 'cancelled';
  }
  return 'pending';
}

const statusBadgeVariant: Record<OrderStatus, GlassBadgeVariant> = {
  pending: 'warning',
  'in-progress': 'info',
  completed: 'success',
  cancelled: 'danger',
};

const statusLabel: Record<OrderStatus, string> = {
  pending: 'Pending',
  'in-progress': 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return '\u2014';
  }
  return new Date(iso).toLocaleString();
}

function formatDate(iso: string | null): string {
  if (iso === null) {
    return '\u2014';
  }
  return new Date(iso).toLocaleDateString();
}

function formatDuration(start: string | null, stop: string | null): string {
  if (start === null) {
    return '\u2014';
  }
  const s = new Date(start);
  const e = stop !== null ? new Date(stop) : new Date();
  const ms = e.getTime() - s.getTime();
  if (ms < 0) {
    return '\u2014';
  }
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${String(h)}h ${String(m)}m` : `${String(m)}m`;
}

/* ── Sub-components ── */

interface DetailFieldProps {
  label: string;
  value: string;
  icon?: ReactElement;
}

function DetailField({ label, value, icon }: DetailFieldProps): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-1.5 text-sm text-text">
        {icon !== undefined && <span className="text-text-tertiary shrink-0">{icon}</span>}
        <span>{value}</span>
      </div>
    </div>
  );
}

interface SectionHeaderProps {
  icon: ReactElement;
  title: string;
  count?: number;
}

function SectionHeader({ icon, title, count }: SectionHeaderProps): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <span className="text-text-secondary">{icon}</span>
      <h2 className="text-base font-semibold text-text">
        {title}
        {count !== undefined && (
          <span className="ml-2 text-sm font-normal text-text-secondary">({String(count)})</span>
        )}
      </h2>
    </div>
  );
}

/* ── Page ── */

export function ServiceReportPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const resolvedId = id ?? '';
  const accessKey = parseFloat(resolvedId);

  const { data, loading, error, refetch } = useGraphQL<ServiceOrderData>(GET_SERVICE_ORDER, {
    accessKey,
  });

  function handleBack(): void {
    void navigate('/orders');
  }

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <Loader2 size={40} className="text-text-tertiary animate-spin" />
        <p className="text-sm text-text-secondary">Loading service report&hellip;</p>
      </div>
    );
  }

  /* ── Error ── */
  if (error !== null) {
    return (
      <div className="space-y-6">
        <div>
          <GlassButton variant="ghost" size="sm" onClick={handleBack} className="-ml-1">
            <ArrowLeft size={14} />
            Back to Orders
          </GlassButton>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Failed to load service report</p>
            <p className="text-xs mt-0.5 text-danger/80">{error}</p>
          </div>
          <GlassButton variant="ghost" size="sm" onClick={refetch} className="shrink-0 text-danger">
            Retry
          </GlassButton>
        </div>
      </div>
    );
  }

  /* ── Not found ── */
  if (data?.serviceOrder === undefined || data.serviceOrder === null) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <ClipboardList size={48} className="text-text-tertiary" />
        <div className="text-center">
          <p className="text-lg font-semibold text-text">Report not found</p>
          <p className="text-sm text-text-secondary mt-1">
            No service order with ID &ldquo;{resolvedId}&rdquo; could be located.
          </p>
        </div>
        <GlassButton variant="secondary" size="md" onClick={handleBack}>
          <ArrowLeft size={14} />
          Back to Orders
        </GlassButton>
      </div>
    );
  }

  const node = data.serviceOrder;
  const status = normaliseStatus(node.status);
  const completedCount = node.units.filter((u) => u.serviceComplete).length;
  const deniedCount = node.units.filter((u) => u.accessDenied).length;

  const fullAddress =
    node.client !== null
      ? [node.client.address, node.client.city, node.client.state, node.client.zip]
          .filter(Boolean)
          .join(', ')
      : '';

  /* Total time from cumulative values */
  const totalMinutes = node.timeRecords.reduce((sum, r) => sum + (r.cummulative ?? 0), 0);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalMins = Math.round(totalMinutes % 60);
  const totalTimeLabel =
    totalMinutes > 0
      ? totalHours > 0
        ? `${String(totalHours)}h ${String(totalMins)}m`
        : `${String(totalMins)}m`
      : null;

  /* ── Render ── */

  return (
    <div className="space-y-6 print:space-y-4">
      {/* ── Toolbar (hidden when printing) ── */}
      <div className="flex items-center justify-between print:hidden">
        <GlassButton variant="ghost" size="sm" onClick={handleBack} className="-ml-1">
          <ArrowLeft size={14} />
          Back to Orders
        </GlassButton>
        <div className="flex items-center gap-2">
          <Suspense
            fallback={
              <GlassButton variant="secondary" size="md" disabled>
                <Download size={14} />
                Loading...
              </GlassButton>
            }
          >
            <PdfDownload
              order={node as unknown as PdfServiceOrder}
              filename={`service-report-${node.ticketno}.pdf`}
            />
          </Suspense>
          <GlassButton
            variant="ghost"
            size="md"
            onClick={() => {
              window.print();
            }}
            aria-label="Print report"
          >
            Print
          </GlassButton>
        </div>
      </div>

      {/* ── Report header (visible in print) ── */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold text-text">Service Report</h1>
        <p className="text-sm text-text-secondary">
          Generated {new Date().toLocaleDateString()} &middot; Ticket {node.ticketno}
        </p>
      </div>

      {/* ── Order Summary ── */}
      <GlassCard>
        <GlassCardContent className="py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-col gap-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-semibold text-text-secondary">
                  {node.ticketno}
                </span>
                <GlassBadge variant={statusBadgeVariant[status]}>{statusLabel[status]}</GlassBadge>
                {node.priority !== '' && (
                  <GlassBadge variant="default">Priority: {node.priority}</GlassBadge>
                )}
              </div>
              <h1 className="text-xl font-semibold text-text mt-1">{node.description}</h1>
              {node.notes !== null && node.notes.trim() !== '' && (
                <p className="text-sm text-text-secondary leading-relaxed mt-0.5">{node.notes}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5 sm:items-end text-sm text-text-secondary shrink-0">
              <div className="flex items-center gap-1.5">
                <Clock size={13} />
                <span>Created: {formatDate(node.createdDate)}</span>
              </div>
              {node.dueDate !== null && (
                <div className="flex items-center gap-1.5">
                  <Calendar size={13} />
                  <span>Scheduled: {formatDate(node.dueDate)}</span>
                </div>
              )}
              {node.completedDate !== null && (
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-success" />
                  <span>Completed: {formatDate(node.completedDate)}</span>
                </div>
              )}
              {totalTimeLabel !== null && (
                <div className="flex items-center gap-1.5">
                  <Clock size={13} />
                  <span>Total time on site: {totalTimeLabel}</span>
                </div>
              )}
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* ── Client Info ── */}
      {node.client !== null && (
        <GlassCard>
          <GlassCardHeader>
            <SectionHeader icon={<Building2 size={16} />} title="Client Information" />
          </GlassCardHeader>
          <GlassCardContent>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <DetailField
                label="Client"
                value={node.client.clientName}
                icon={<Building2 size={13} />}
              />
              {fullAddress !== '' && (
                <DetailField label="Address" value={fullAddress} icon={<MapPin size={13} />} />
              )}
              {node.client.contact !== null && (
                <DetailField
                  label="Site Contact"
                  value={node.client.contact}
                  icon={<User size={13} />}
                />
              )}
              {node.client.phone !== null && (
                <DetailField label="Phone" value={node.client.phone} icon={<Phone size={13} />} />
              )}
              {node.client.email !== null && (
                <DetailField label="Email" value={node.client.email} />
              )}
              <DetailField label="Client Code" value={node.client.clientCode} />
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* ── Service Details ── */}
      <GlassCard>
        <GlassCardHeader>
          <SectionHeader icon={<Wrench size={16} />} title="Service Details" />
        </GlassCardHeader>
        <GlassCardContent>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <DetailField label="Status" value={statusLabel[status]} />
            <DetailField label="Priority" value={node.priority !== '' ? node.priority : 'Normal'} />
            <DetailField
              label="Assigned Technician"
              value={node.assignedTo !== '' ? node.assignedTo : '\u2014'}
              icon={<User size={13} />}
            />
            {node.dueDate !== null && (
              <DetailField
                label="Scheduled Date"
                value={formatDate(node.dueDate)}
                icon={<Calendar size={13} />}
              />
            )}
            {node.completedDate !== null && (
              <DetailField
                label="Completion Date"
                value={formatDate(node.completedDate)}
                icon={<Clock size={13} />}
              />
            )}
            {totalTimeLabel !== null && (
              <DetailField
                label="Total Time on Site"
                value={totalTimeLabel}
                icon={<Clock size={13} />}
              />
            )}
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* ── Unit Breakdown ── */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <SectionHeader
              icon={<ClipboardList size={16} />}
              title="Unit Breakdown"
              count={node.units.length}
            />
            {node.units.length > 0 && (
              <div className="flex items-center gap-2">
                <GlassBadge variant="success">{String(completedCount)} Complete</GlassBadge>
                {deniedCount > 0 && (
                  <GlassBadge variant="danger">{String(deniedCount)} Denied</GlassBadge>
                )}
                <GlassBadge variant="default">
                  {String(node.units.length - completedCount - deniedCount)} Pending
                </GlassBadge>
              </div>
            )}
          </div>
        </GlassCardHeader>
        {node.units.length === 0 ? (
          <GlassCardContent>
            <p className="text-sm text-text-secondary text-center py-6">
              No units are associated with this service order.
            </p>
          </GlassCardContent>
        ) : (
          <GlassTable>
            <GlassTableHead>
              <GlassTableRow>
                <GlassTableHeaderCell>Unit</GlassTableHeaderCell>
                <GlassTableHeaderCell className="hidden sm:table-cell">Floor</GlassTableHeaderCell>
                <GlassTableHeaderCell className="hidden md:table-cell">
                  Location
                </GlassTableHeaderCell>
                <GlassTableHeaderCell className="text-center">Status</GlassTableHeaderCell>
              </GlassTableRow>
            </GlassTableHead>
            <GlassTableBody>
              {node.units.map((unit) => (
                <GlassTableRow key={String(unit.accessKey)}>
                  <GlassTableCell>
                    <span className="font-mono text-xs font-semibold text-primary">
                      {unit.unitNo}
                    </span>
                    {unit.floor !== null && (
                      <p className="text-xs text-text-tertiary mt-0.5 sm:hidden">{unit.floor}</p>
                    )}
                  </GlassTableCell>
                  <GlassTableCell className="text-text-secondary hidden sm:table-cell">
                    {unit.floor ?? '\u2014'}
                  </GlassTableCell>
                  <GlassTableCell className="text-text-secondary hidden md:table-cell">
                    {unit.location ?? '\u2014'}
                  </GlassTableCell>
                  <GlassTableCell className="text-center">
                    {unit.accessDenied ? (
                      <GlassBadge variant="danger">Access Denied</GlassBadge>
                    ) : unit.serviceComplete ? (
                      <div className="inline-flex items-center gap-1 text-success text-xs font-medium">
                        <CheckCircle2 size={14} />
                        <span>Complete</span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1 text-text-tertiary text-xs">
                        <XCircle size={14} />
                        <span>Pending</span>
                      </div>
                    )}
                  </GlassTableCell>
                </GlassTableRow>
              ))}
            </GlassTableBody>
          </GlassTable>
        )}
      </GlassCard>

      {/* ── Time Records ── */}
      {node.timeRecords.length > 0 && (
        <GlassCard>
          <GlassCardHeader>
            <SectionHeader
              icon={<Clock size={16} />}
              title="Time Records"
              count={node.timeRecords.length}
            />
          </GlassCardHeader>
          <GlassTable>
            <GlassTableHead>
              <GlassTableRow>
                <GlassTableHeaderCell>Clock In</GlassTableHeaderCell>
                <GlassTableHeaderCell className="hidden sm:table-cell">
                  Clock Out
                </GlassTableHeaderCell>
                <GlassTableHeaderCell className="hidden md:table-cell">
                  Clock In Address
                </GlassTableHeaderCell>
                <GlassTableHeaderCell className="text-right">Duration</GlassTableHeaderCell>
                <GlassTableHeaderCell className="hidden lg:table-cell">Status</GlassTableHeaderCell>
              </GlassTableRow>
            </GlassTableHead>
            <GlassTableBody>
              {node.timeRecords.map((record) => {
                const loc = record.timeLocation;
                const clockInTime = loc?.clockInTime ?? null;
                const clockOutTime = loc?.clockOutTime ?? null;
                const duration = formatDuration(clockInTime, clockOutTime);

                return (
                  <GlassTableRow key={String(record.id)}>
                    <GlassTableCell>
                      <p className="text-text text-xs whitespace-nowrap">
                        {formatDateTime(clockInTime)}
                      </p>
                      {record.notes !== null && (
                        <p className="text-xs text-text-tertiary mt-0.5 line-clamp-1">
                          {record.notes}
                        </p>
                      )}
                    </GlassTableCell>
                    <GlassTableCell className="text-text-secondary text-xs whitespace-nowrap hidden sm:table-cell">
                      {formatDateTime(clockOutTime)}
                    </GlassTableCell>
                    <GlassTableCell className="text-text-secondary text-xs hidden md:table-cell">
                      <span className="line-clamp-1">{loc?.clockInAddress ?? '\u2014'}</span>
                    </GlassTableCell>
                    <GlassTableCell className="text-right">
                      <span className="font-mono text-xs font-semibold text-text">{duration}</span>
                      {record.cummulative !== null && (
                        <p className="text-xs text-text-tertiary mt-0.5 whitespace-nowrap">
                          {record.cummulative.toFixed(1)} min
                        </p>
                      )}
                    </GlassTableCell>
                    <GlassTableCell className="hidden lg:table-cell">
                      {record.status !== null ? (
                        <GlassBadge
                          variant={
                            record.status === 'active'
                              ? 'success'
                              : record.status === 'paused'
                                ? 'warning'
                                : 'default'
                          }
                        >
                          {record.status}
                        </GlassBadge>
                      ) : (
                        <span className="text-text-tertiary text-xs">&mdash;</span>
                      )}
                    </GlassTableCell>
                  </GlassTableRow>
                );
              })}
            </GlassTableBody>
          </GlassTable>
        </GlassCard>
      )}

      {/* ── Photos ── */}
      {node.images.length > 0 && (
        <GlassCard>
          <GlassCardHeader>
            <SectionHeader icon={<Camera size={16} />} title="Photos" count={node.images.length} />
          </GlassCardHeader>
          <GlassCardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
              {node.images.map((img) => {
                const src =
                  img.thumbnailUrl ?? (img.cfImageId !== null ? `/images/${img.cfImageId}` : null);
                return (
                  <div
                    key={String(img.id)}
                    className="group flex flex-col gap-1.5 rounded-lg border border-[var(--glass-border)] overflow-hidden bg-[var(--glass-bg)]"
                  >
                    {src !== null ? (
                      <img
                        src={src}
                        alt={img.filename}
                        className="w-full aspect-square object-cover group-hover:opacity-90 transition-opacity"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center bg-bg-muted">
                        <ImageIcon size={24} className="text-text-tertiary" />
                      </div>
                    )}
                    <div className="px-2 pb-2">
                      <p className="text-xs font-medium text-text truncate" title={img.filename}>
                        {img.filename}
                      </p>
                      <p className="text-xs text-text-tertiary">
                        {formatDate(img.uploadedDate)}
                        {img.uploadedBy !== null && <span> &middot; {img.uploadedBy}</span>}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* ── Notes ── */}
      {node.notes !== null && node.notes.trim() !== '' && (
        <GlassCard>
          <GlassCardHeader>
            <SectionHeader icon={<MessageSquare size={16} />} title="Notes" />
          </GlassCardHeader>
          <GlassCardContent>
            <p className="text-sm text-text leading-relaxed whitespace-pre-wrap">{node.notes}</p>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* ── Export footer ── */}
      <GlassCard className="print:hidden">
        <GlassCardContent className="py-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-text">Export this report</p>
              <p className="text-xs text-text-secondary mt-0.5">
                Download a formatted PDF or use your browser&apos;s print function.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Suspense
                fallback={
                  <GlassButton variant="secondary" size="md" disabled>
                    <Download size={14} />
                    Loading...
                  </GlassButton>
                }
              >
                <PdfDownload
                  order={node as unknown as PdfServiceOrder}
                  filename={`service-report-${node.ticketno}.pdf`}
                />
              </Suspense>
              <GlassButton
                variant="ghost"
                size="md"
                onClick={() => {
                  window.print();
                }}
              >
                Print
              </GlassButton>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* ── Print footer ── */}
      <div className="hidden print:block mt-8 pt-4 border-t border-gray-200 text-xs text-gray-500 text-center">
        MerriTrac Service Report &mdash; {node.ticketno} &mdash; Generated{' '}
        {new Date().toLocaleString()}
      </div>

      {/* ── Print media overrides ── */}
      <style>{`
        @media print {
          body { background: white !important; }
          .glass, [class*="glass-"] { background: white !important; border: 1px solid #e5e7eb !important; box-shadow: none !important; }
          button, [data-print-hidden] { display: none !important; }
        }
      `}</style>
    </div>
  );
}
